/**
 * A utility class for working with translation objects:
 * organizing keys, building key trees, and performing deep get/set operations.
 */
class TranslationKeysHelper {
  /**
   * Constructor that stores original translations and precomputes organized keys.
   * @param {object} translations - The original translation object.
   * @param {number} [levelLimit=2] - Max depth level for organizing.
   */
  constructor(translations, levelLimit = 2) {
    this.translations = translations;
    this.organized = this.organizeLevels(levelLimit);
  }
  /**
   * Organizes translation keys into a nested structure with a level depth limit.
   * @param {number} levelLimit - Maximum allowed nesting depth.
   * @returns {object}
   */
  organizeLevels(levelLimit = 2) {
    const organize = (obj, limit, currentLevel = 1) => {
      if (currentLevel > limit || typeof obj !== 'object' || obj === null) return obj;

      const result = {};
      for (const key in obj) {
        const value = obj[key];

        if (typeof value === 'object' && value !== null && currentLevel < limit) {
          result[key] = organize(value, limit, currentLevel + 1);
        } else if (typeof value === 'object' && value !== null && currentLevel === limit) {
          const flatten = (v, prefix = "") => {
            let flat = {};
            for (const k in v) {
              const subValue = v[k];
              const compoundKey = prefix ? `${prefix}.${k}` : k;
              if (typeof subValue === 'object' && subValue !== null) {
                Object.assign(flat, flatten(subValue, compoundKey));
              } else {
                flat[compoundKey] = subValue;
              }
            }
            return flat;
          };
          Object.assign(result, flatten(value, key));
        } else {
          result[key] = value;
        }
      }

      return result;
    };

    return organize(this.translations, levelLimit, 1);
  }

  /**
   * Builds a nested key tree from flat translations.
   * @param {object} flatTranslations - Flat key translation object.
   * @returns {object}
   */
  buildKeysTree(flatTranslations) {
    if (typeof flatTranslations === 'string') return flatTranslations;
    const result = {};

    for (const [flatKey, value] of Object.entries(flatTranslations)) {
      const keys = flatKey.split('.');
      let current = result;

      keys.forEach((key, index) => {
        const isLast = index === keys.length - 1;
        if (isLast) {
          current[key] = this.buildKeysTree(value);
        } else {
          if (
            typeof current[key] !== 'object' ||
            current[key] === null ||
            Array.isArray(current[key])
          ) {
            current[key] = {};
          }
          current = current[key];
        }
      });
    }

    return result;
  }

  /**
   * Sets a value at a specific path (dot notation).
   * @param {string} keyPath - Dot-notated path.
   * @param {*} value - Value to set.
   * @returns {boolean}
   */
  setDeepKeyValue(keyPath, value) {
    const parts = keyPath.split('.');

    const recursiveSet = (obj, parts) => {
      if (typeof obj !== 'object' || obj === null) return false;

      const [first, ...rest] = parts;

      if (rest.length === 0) {
        if (first in obj) {
          obj[first] = value;
          return true;
        }
        const joinedKey = parts.join('.');
        if (joinedKey in obj) {
          obj[joinedKey] = value;
          return true;
        }
        return false;
      }

      if (first in obj) {
        return recursiveSet(obj[first], rest);
      }

      for (const key in obj) {
        if (typeof obj[key] === 'object') {
          if (recursiveSet(obj[key], parts)) return true;
        }
      }

      const joinedKey = parts.join('.');
      if (joinedKey in obj) {
        obj[joinedKey] = value;
        return true;
      }

      return false;
    };

    // Set the value in both translations and organized objects to ensure consistency
    const translationsChanged = recursiveSet(this.translations, parts);
    const organizedChanged = recursiveSet(this.organized, parts);
    return translationsChanged && organizedChanged;
  }

  /**
   * Retrieves a value by a specific path (dot notation).
   * @param {string} keyPath - Dot-notated path.
   * @returns {*}
   */
  getDeepKeyValue(keyPath) {
    const parts = keyPath.split('.');

    const recursiveGet = (obj, parts) => {
      if (typeof obj !== 'object' || obj === null) return undefined;

      const [first, ...rest] = parts;

      if (rest.length === 0) {
        if (first in obj) return obj[first];
        const joinedKey = parts.join('.');
        if (joinedKey in obj) return obj[joinedKey];
        return undefined;
      }

      if (first in obj) {
        return recursiveGet(obj[first], rest);
      }

      for (const key in obj) {
        if (typeof obj[key] === 'object') {
          const result = recursiveGet(obj[key], parts);
          if (result !== undefined) return result;
        }
      }

      const joinedKey = parts.join('.');
      if (joinedKey in obj) return obj[joinedKey];

      return undefined;
    };

    return recursiveGet(this.translations, parts);
  }
}

export default TranslationKeysHelper;